import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 60000, // 60 seconds - increased to handle potentially long-running delete operations
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable sending cookies for SSO authentication
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Attempt to refresh token
        const response = await axios.post('/api/auth/refresh', {
          refreshToken
        });

        const { accessToken } = response.data;
        localStorage.setItem('token', accessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        
        // Only redirect if we're not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to handle file uploads
export const uploadFile = async (endpoint, file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post(endpoint, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent) => {
      if (onProgress) {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        onProgress(percentCompleted);
      }
    },
  });
};

// Helper function to download files
export const downloadFile = async (endpoint, filename) => {
  try {
    const response = await api.get(endpoint, {
      responseType: 'blob',
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Download failed:', error);
    return { 
      success: false, 
      error: error.response?.data?.message || 'Download failed' 
    };
  }
};

// API endpoints object for better organization
export const endpoints = {
  // Authentication
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
    register: '/auth/register'
  },

  // Dashboard
  dashboard: {
    summary: '/dashboard/summary',
    recentActivity: '/dashboard/recent-activity',
    trending: '/dashboard/trending'
  },

  // Analytics
  analytics: {
    overview: '/analytics/overview',
    features: '/analytics/features',
    developers: '/analytics/users',
    developerDetails: (userId) => `/analytics/user-details?userId=${userId}`,
    teams: '/analytics/teams',
    export: '/analytics/export',
    requestDistribution: '/analytics/request-distribution',
    dailyModelUsage: '/analytics/daily-model-usage',
    kpis: '/analytics/kpis',
    dailyTrends: '/analytics/daily-trends'
  },

  // Upload endpoints removed - CSV upload functionality deprecated

  // Admin
  admin: {
    health: '/admin/health',
    users: '/admin/users',  // Application login users (AuthUsers table)
    csvUsers: '/admin/csv-users',  // CSV imported users (Users table)
    userRole: (userId) => `/admin/users/${userId}/role`,
    userTeam: (userId) => `/admin/users/${userId}/team`,  // For CSV users
    deleteUser: (userId) => `/admin/users/${userId}`,
    reInviteUser: (userId) => `/admin/users/${userId}/re-invite`,
    teams: '/admin/teams',
    createTeam: '/admin/teams',
    updateTeamFriendlyName: (teamId) => `/admin/teams/${teamId}/friendly-name`,

    assignTeams: '/admin/assign-teams',
    nuclear: '/admin/nuclear',
    
    // SendGrid endpoints
    sendgrid: {
      config: '/admin/sendgrid/config',
      test: '/admin/sendgrid/test',
      teamsPage: '/admin/sendgrid/teams-page'
    }
  },

  // Cursor API Integration
  cursor: {
    testConnection: '/cursor/test-connection',
    importManual: '/cursor/import-manual',
    importRecent: '/cursor/import-recent',
    importHistory: '/cursor/import-history',
    databaseStatus: '/cursor/database-status',
    databaseQuickStatus: '/cursor/database-quick-status',
    dataStats: '/cursor/data-stats',
    nukeAll: '/cursor/nuke-all',
    nukeDateRange: '/cursor/nuke-date-range',
    nukeCsv: '/cursor/nuke-csv',
    teamMembers: '/cursor/team-members',
    testImport: '/cursor/test-import',
    schedulerStatus: '/cursor/scheduler/status',
    schedulerTrigger: '/cursor/scheduler/trigger',
    schedulerHistory: '/cursor/scheduler/history',
    schedulerConfig: '/cursor/scheduler/config',
    schedulerTimezones: '/cursor/scheduler/timezones',
    debugDatabaseLines: '/cursor/debug-database-lines',
    debugLinesAdded: '/cursor/debug-lines-added',
    debugDatabaseLinesRange: (start, end) => `/cursor/debug-database-lines-range?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`
  },

  // Billing Groups / Monthly Usage
  billingGroups: {
    summary: '/billing-groups/summary',
    annualSummary: '/billing-groups/annual-summary',
    availableMonths: '/billing-groups/available-months',
    settings: '/billing-groups/settings',
    import: '/billing-groups/import',
    backfill: '/billing-groups/backfill',
    imports: '/billing-groups/imports',
    testConnection: '/billing-groups/test-connection',
    migrate: '/billing-groups/migrate',
    schedulerStatus: '/billing-groups/scheduler/status',
    schedulerConfig: '/billing-groups/scheduler/config',
    schedulerTrigger: '/billing-groups/scheduler/trigger',
    refreshCurrentMonth: '/billing-groups/refresh-current-month',
    prepaid: '/billing-groups/prepaid',
    costAllocation: '/billing-groups/cost-allocation'
  },

  monthlyUsageReport: {
    config: '/monthly-usage-report/config',
    schedules: '/monthly-usage-report/schedules',
    schedule: (id) => `/monthly-usage-report/schedules/${id}`,
    runSchedule: (id) => `/monthly-usage-report/schedules/${id}/run`,
    generate: '/monthly-usage-report/generate',
    history: '/monthly-usage-report/history'
  }
};

// Utility functions for common API patterns
export const apiUtils = {
  // Handle paginated requests
  getPaginated: async (endpoint, page = 1, limit = 20, params = {}) => {
    const response = await api.get(endpoint, {
      params: { page, limit, ...params }
    });
    return response.data;
  },

  // Handle date range queries
  getDateRange: async (endpoint, startDate, endDate, params = {}) => {
    const response = await api.get(endpoint, {
      params: { startDate, endDate, ...params }
    });
    return response.data;
  },

  // Handle bulk operations
  bulkOperation: async (endpoint, items, operation) => {
    const response = await api.post(endpoint, {
      items,
      operation
    });
    return response.data;
  }
};

export default api; 